import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { KeyRound, Loader2, Copy, X, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PasswordResetRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [customPwd, setCustomPwd] = useState('');
  const [generated, setGenerated] = useState(null);
  const [working, setWorking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/password-reset-requests`, { withCredentials: true });
      setRequests(res.data.requests || []);
    } catch (e) {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openResolve = (req) => {
    setSelected(req);
    setCustomPwd('');
    setGenerated(null);
    setResolveOpen(true);
  };

  const resolve = async () => {
    setWorking(true);
    try {
      const payload = customPwd ? { new_password: customPwd } : {};
      const res = await axios.post(
        `${API}/admin/password-reset-requests/${selected.id}/resolve`,
        payload,
        { withCredentials: true }
      );
      setGenerated(res.data.temporary_password);
      toast.success('Reset complete — share the password with the user securely');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resolve');
    } finally {
      setWorking(false);
    }
  };

  const cancel = async (req) => {
    if (!window.confirm(`Cancel reset request for ${req.email}?`)) return;
    try {
      await axios.delete(`${API}/admin/password-reset-requests/${req.id}`, { withCredentials: true });
      toast.success('Request cancelled');
      load();
    } catch (e) {
      toast.error('Failed to cancel');
    }
  };

  const copyPwd = () => {
    if (generated) {
      navigator.clipboard.writeText(generated);
      toast.success('Copied');
    }
  };

  return (
    <div className="space-y-6" data-testid="password-reset-requests-page">
      <div>
        <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
          <KeyRound className="h-7 w-7 text-primary" /> Password Reset Requests
        </h1>
        <p className="text-muted-foreground mt-1">
          Users who clicked "Forgot password" show up here. Issue a temporary password
          and share it with them — they'll be forced to change it on first login.
        </p>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>Pending requests ({requests.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : requests.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">No pending requests</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.name || '—'}</TableCell>
                    <TableCell className="font-mono text-xs">{r.email}</TableCell>
                    <TableCell className="capitalize">{r.role}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={() => openResolve(r)}
                        data-testid={`resolve-${r.id}`}
                      >
                        <Check className="h-4 w-4 mr-1" /> Resolve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => cancel(r)}
                        className="ml-2"
                        data-testid={`cancel-${r.id}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Resolve password reset</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="text-sm">
                <p><b>User:</b> {selected.name}</p>
                <p className="font-mono text-xs">{selected.email}</p>
              </div>

              {generated ? (
                <div className="space-y-3">
                  <p className="text-sm">Temporary password (share securely):</p>
                  <div className="flex items-center gap-2 p-3 bg-muted/40 rounded border">
                    <code className="flex-1 font-mono text-sm select-all">{generated}</code>
                    <Button size="sm" variant="outline" onClick={copyPwd} data-testid="copy-otp-button">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    User will be forced to change this on next login. Close this dialog when
                    you've shared it.
                  </p>
                  <Button className="w-full" onClick={() => setResolveOpen(false)}>
                    Done
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Leave blank to auto-generate a strong one-time password, or set a custom
                    one (must be ≥ 8 chars with upper/lower/digit/special).
                  </p>
                  <Input
                    placeholder="Optional: custom password"
                    value={customPwd}
                    onChange={(e) => setCustomPwd(e.target.value)}
                    data-testid="custom-otp-input"
                  />
                  <Button
                    className="w-full"
                    onClick={resolve}
                    disabled={working}
                    data-testid="confirm-resolve-button"
                  >
                    {working ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {customPwd ? 'Set this password' : 'Generate one-time password'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PasswordResetRequestsPage;
